import { CommonModule } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    ElementRef,
    inject,
    input,
    OnChanges,
    OnDestroy,
    OnInit,
    Renderer2,
    SimpleChanges
} from '@angular/core';
import { App } from '@df/core/app';
import { ElementHeightDirective } from '@df/dom/element-height/element-height.directive';
import { ElementHeightService } from '@df/dom/element-height/element-height.service';
import { injectTimeoutService } from '@df/ng/timeout.service';
import { BlockStickyService } from '@df/ui/block-sticky.service';
import { ScrollService } from '@df/util/service/scroll.service';
import { BehaviorSubject, map, merge, of, switchMap } from 'rxjs';
import { Subscription } from 'rxjs/internal/Subscription';
import { StickyFreeService } from './sticky-free.service';

let uid = 0;

@Component({
    selector: 'sticky-free',
    templateUrl: './sticky-free.html',
    styleUrl: './sticky-free.scss',
    standalone: true,
    imports: [CommonModule, ElementHeightDirective],
    host: {
        '[class.is-range-setter]': 'isRangeSetter()'
    },
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class StickyFreeComponent implements OnInit, OnDestroy, OnChanges {
    protected readonly _uid = `stickyfree${uid++}`;

    order = input<number>(0);
    stickyOff = input<boolean>(false);
    stickyId = input<string>();
    stickyOffset = input<number | string>(0);
    isRangeSetter = input<boolean>(false);

    uid = computed(() => this.stickyId() || this._uid);

    protected isSticky = false;
    protected isStickyOn$ = new BehaviorSubject<boolean>(false);
    protected initiated = false;

    protected unregister?: () => void;
    protected subscription?: Subscription;
    protected initialSubscription?: Subscription;
    protected subscriptions = new Subscription();
    protected scrollSubscription?: Subscription;

    protected scrollService = inject(ScrollService);
    protected renderer = inject(Renderer2);
    protected elementRef = inject(ElementRef);
    protected stickyFreeService = inject(StickyFreeService);
    protected elementHeightService = inject(ElementHeightService);
    protected app = inject(App);
    protected timeoutService = injectTimeoutService();
    protected blockStickyService = inject(BlockStickyService);

    ngOnInit(): void {
        if (this.app.isPlatformBrowser) {
            this.applySticky();

            this.initialSubscription = this.elementHeightService.getState(this.uid()).subscribe(value => {
                if (value !== undefined) {
                    this.renderer.setStyle(this.elementRef.nativeElement, 'height', this.stickyFreeService.toVar(this.uid()));
                    this.renderer.addClass(this.elementRef.nativeElement, 'is-ready');
                    // const isSafari = true || /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
                    // // its only needed for safari below 15.4, we can drop it in about 1y
                    // if (isSafari) {
                    this.elementRef.nativeElement.style.setProperty(
                        '--in-app-shift',
                        `${this.elementRef.nativeElement.getBoundingClientRect().top + this.app.client.getRootElement().scrollTop}px`
                    );
                    // }
                    this.timeoutService.setTimeout(() => {
                        this.initialSubscription?.unsubscribe();
                        this.initialSubscription = undefined;
                    }, 0);
                }
            });

            this.scrollSubscription = this.isStickyOn$
                .pipe(
                    switchMap(value =>
                        value
                            ? merge(this.scrollService.scroll$, this.app.meta.routerEvents).pipe(
                                map(() => {
                                    const top = this.elementRef.nativeElement.getBoundingClientRect().top;
                                    let stickyOffset = 0;

                                    if (this.stickyOffset() !== undefined) {
                                        stickyOffset = !Number.isNaN(Number(this.stickyOffset()))
                                            ? Number(this.stickyOffset())
                                            : this.elementHeightService.getState(`${this.stickyOffset()}`).value || 0;
                                    } else if (this.order() > 0) {
                                        stickyOffset = this.stickyFreeService.getOffsetsNumber(this.order(), true);
                                    }

                                    this.updateIsStickyClass(top < stickyOffset);

                                    if (this.isRangeSetter()) {
                                        const scrollTop = document.scrollingElement?.scrollTop;

                                        this.stickyFreeService.setStickyRange(`${top + scrollTop}px`);
                                    }
                                })
                            )
                            : of(null)
                    )
                )
                .subscribe();

            this.subscriptions.add(this.scrollService.isScrollDown$.subscribe(value => this.scrollDown(!!value)));
            this.subscriptions.add(this.blockStickyService.state$.subscribe(() => this.initiated && this.applySticky()));

            this.initiated = true;
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (this.initiated) {
            if (changes['stickyOff']) {
                this.applySticky();
            }

            if (changes['isRangeSetter'] && !this.isRangeSetter()) {
                this.stickyFreeService.setStickyRange(undefined);
            }
        }
    }

    ngOnDestroy(): void {
        this.unsetSticky();
        this.initialSubscription?.unsubscribe();
        this.subscriptions.unsubscribe();
        this.scrollSubscription?.unsubscribe();

        if (this.isRangeSetter()) {
            this.stickyFreeService.setStickyRange(undefined);
        }
    }

    protected applySticky() {
        if (this.stickyOff() || this.blockStickyService.state$.value) {
            this.unsetSticky();
        } else {
            this.setSticky();
        }
    }

    protected setSticky() {
        if (this.isStickyOn$.value) {
            return;
        }

        this.isStickyOn$.next(true);
        this.renderer.removeClass(this.elementRef.nativeElement, 'is-off');

        if (this.order !== undefined) {
            this.subscription = this.stickyFreeService.entriesChange.subscribe(() => {
                this.elementRef.nativeElement.style.setProperty('--sticky-offset', this.stickyFreeService.getOffsets(this.order() || 0));
            });

            this.unregister = this.stickyFreeService.add({
                uid: this.uid(),
                order: this.order()
            });
        }
    }

    protected unsetSticky() {
        if (!this.isStickyOn$.value) {
            return;
        }

        this.isStickyOn$.next(false);
        this.renderer.addClass(this.elementRef.nativeElement, 'is-off');

        if (this.unregister) {
            this.unregister();
            this.unregister = undefined;
        }

        if (this.subscription) {
            this.subscription.unsubscribe();
            this.subscription = undefined;
        }
    }

    protected updateIsStickyClass(isSticky = this.isSticky) {
        if (!this.isStickyOn$.value) {
            return;
        }

        if (this.isSticky !== isSticky) {
            if (isSticky) {
                this.renderer.addClass(this.elementRef.nativeElement, 'is-sticky');
            } else {
                this.renderer.removeClass(this.elementRef.nativeElement, 'is-sticky');
            }

            this.isSticky = isSticky;
        }
    }

    protected scrollDown(value: boolean) {
        if (!this.isStickyOn$.value) {
            return;
        }

        if (value) {
            this.renderer.addClass(this.elementRef.nativeElement, 'is-scroll-down');
        } else {
            this.renderer.removeClass(this.elementRef.nativeElement, 'is-scroll-down');
        }
    }
}
